import { useEffect } from 'react'
import { Box, Menu, MenuButton, MenuItem, MenuList, VStack, Text, HStack, IconButton, Spinner } from '@chakra-ui/react'
import { NotificationTypeEnum } from 'configs/constants/enums/notification'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { observer } from 'mobx-react'
import { toast } from 'react-toastify'
import { seenNotification } from 'API/notification'
import SvgIcon from 'components/SvgIcon'
import CommentNotification from 'components/Topbar/components/NotificationBell/components/CommentNofication'
import DeleteCommonStepNotification from 'components/Topbar/components/NotificationBell/components/DeleteCommonStepNotification'
import OrgInvitationNotification from 'components/Topbar/components/NotificationBell/components/OrgInvitationNotification'
import UpdateCommonStepNotification from 'components/Topbar/components/NotificationBell/components/UpdateCommonStepNotification'
// Reuse the same SCSS that sizes avatars + text inside the Topbar bell. The nested
// selectors `.notificationItem .notificationContent .avatar img { 34px }` only apply
// when the row is wrapped in those parent classes — without them, images render at
// natural size and the modal looks broken.
import bellStyles from 'components/Topbar/components/NotificationBell/styles.module.scss'
import { useStores } from 'hooks/useStores'

dayjs.extend(relativeTime)

/**
 * Dark-themed bell for /play-dashboard. Uses the same notificationStore as the
 * Topbar bell (account-keyed list owned by the backend); styling adapted for the
 * page's dark palette (bg #0a1322, white text, alpha surfaces).
 */
const PlayDashboardBell = () => {
  const { notificationStore, authStore } = useStores()
  const { notifications, isFetching } = notificationStore
  // Reactive count derived from the local notifications list — flips to N-1
  // immediately when acceptInvitation/denyInvitation mutate isSeen. The legacy
  // `countNotifications` field is only refreshed by aggregateCountNotifications()
  // and would stay stale after local accept/deny actions (the bug user reported).
  const countNotifications = notificationStore.notificationCountForActiveProfile

  useEffect(() => {
    if (!authStore?.userDetail?.id) return
    notificationStore.fetchNotifications()
    notificationStore.aggregateCountNotifications()
  }, [authStore?.userDetail?.id, notificationStore])

  const handleMarkAllRead = async () => {
    try {
      await notificationStore.seenAllNotifications()
      toast.success('All notifications marked as read')
      notificationStore.fetchNotifications()
    } catch {
      toast.error('Could not mark all as read')
    }
  }

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label="Notifications"
        bg="transparent"
        color="#F4F8FB"
        borderRadius="full"
        w="36px"
        h="36px"
        border="none"
        position="relative"
        _hover={{ bg: 'rgba(255,255,255,0.08)' }}
        _active={{ bg: 'rgba(255,255,255,0.12)' }}
        onClick={() => notificationStore.fetchNotifications()}
        icon={
          <Box position="relative">
            <SvgIcon iconName="notification-bell" size={20} color="#F4F8FB" />
            {countNotifications > 0 && (
              <Box
                position="absolute"
                top="-4px"
                right="-6px"
                minW="14px"
                h="14px"
                px="3px"
                borderRadius="full"
                bg="#ef4444"
                color="white"
                fontSize="9px"
                lineHeight="14px"
                textAlign="center"
                fontWeight={600}
              >
                {countNotifications > 99 ? '99+' : countNotifications}
              </Box>
            )}
          </Box>
        }
      />
      <MenuList
        bg="#12203a"
        borderColor="rgba(255,255,255,0.1)"
        color="white"
        maxH="60vh"
        overflowY="auto"
        w="360px"
        p={0}
        boxShadow="0 12px 32px rgba(0,0,0,0.4)"
        sx={{
          // Chakra arrow-key navigation paints a focus ring (box-shadow) on the
          // active MenuItem. On a dark surface it reads as an ugly white outline.
          // Kill it across all MenuItem states.
          '.chakra-menu__menuitem': {
            outline: 'none',
            border: 'none'
          },
          '.chakra-menu__menuitem:focus, .chakra-menu__menuitem:focus-visible, .chakra-menu__menuitem[data-focus]': {
            boxShadow: 'none',
            outline: 'none'
          }
        }}
      >
        <HStack
          justify="space-between"
          px={4}
          py={3}
          borderBottom="1px solid rgba(255,255,255,0.08)"
          position="sticky"
          top={0}
          bg="#12203a"
          zIndex={1}
        >
          <Text fontWeight={600} m={0}>
            Notifications
          </Text>
          <Text
            as="button"
            type="button"
            fontSize="sm"
            color="#3b82f6"
            background="transparent"
            border="none"
            onClick={handleMarkAllRead}
            _hover={{ textDecoration: 'underline' }}
          >
            Mark all as read
          </Text>
        </HStack>
        {/* Loading: while notifications for the active profile are still being
            fetched (e.g. mid profile-switch) show a spinner instead of a stale
            list or a premature "No notifications yet". */}
        {isFetching && notifications.length === 0 && (
          <Box px={4} py={8} textAlign="center">
            <Spinner size="md" color="#3b82f6" thickness="3px" speed="0.6s" />
          </Box>
        )}
        {!isFetching && notifications.length === 0 && (
          <Box px={4} py={6} textAlign="center" opacity={0.6}>
            <Text fontSize="sm" m={0}>
              No notifications yet
            </Text>
          </Box>
        )}
        {notifications.map((notification, idx) => (
          <MenuItem
            key={notification.id ?? idx}
            bg={notification.isSeen ? 'transparent' : 'rgba(59,130,246,0.08)'}
            color="white"
            _hover={{ bg: 'rgba(255,255,255,0.04)' }}
            _focus={{ bg: 'rgba(255,255,255,0.04)', boxShadow: 'none' }}
            _focusVisible={{ boxShadow: 'none' }}
            px={3}
            py={2}
            onClick={() => {
              // ORG_INVITATION rows own their own actions (Accept / Deny buttons);
              // mark seen but never navigate. Other notification types follow today's
              // mark-seen-only behavior here (no navigation from this dashboard).
              seenNotification(notification?.id ?? 0)
            }}
          >
            {/* Wrap in the bell's SCSS classes so nested selectors (.avatar img, .textContent)
                size the avatar and text consistently with the org-dashboard bell.
                Override the SCSS `.textContent { color: #323a46 }` so text is readable
                on the dark surface — match the higher-specificity nested selector. */}
            <Box
              className={bellStyles.notificationItem}
              w="100%"
              sx={{
                // SCSS rule has 3-class specificity (.notificationItem .notificationContent
                // .textContent { color: #323a46 }) — !important wins on the dark surface.
                [`& .${bellStyles.textContent}`]: { color: 'white !important' },
                [`& .${bellStyles.object}`]: { color: 'white !important' }
              }}
            >
              <HStack align="flex-start" justify="space-between" w="100%" spacing={2}>
                <Box className={bellStyles.notificationContent} flex={1} minW={0}>
                  {notification?.type === NotificationTypeEnum.COMMENT_NOTIFICATION && (
                    <CommentNotification notification={notification} />
                  )}
                  {notification?.type === NotificationTypeEnum.UPDATED_COMMON_STEP_NOTIFICATION && (
                    <UpdateCommonStepNotification notification={notification} />
                  )}
                  {notification?.type === NotificationTypeEnum.DELETED_COMMON_STEP_NOTIFICATION && (
                    <DeleteCommonStepNotification notification={notification} />
                  )}
                  {notification?.type === NotificationTypeEnum.ORG_INVITATION && (
                    <OrgInvitationNotification notification={notification} />
                  )}
                </Box>
                <Text fontSize="xs" opacity={0.5} whiteSpace="nowrap">
                  {dayjs(notification?.createdAt).fromNow(true)}
                </Text>
              </HStack>
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  )
}

export default observer(PlayDashboardBell)
